/**
 * JobDetails Component
 *
 * Displays detailed information about a specific job position.
 * Layout tokens: JobDetailsPage theme (useMultiStyleConfig).
 *
 * Requirements:
 * - JA-005: Job details component
 *
 * @module components/careers/job-details
 */

import React from 'react';
import PropTypes from 'prop-types';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import {
  Box,
  Divider,
  Heading,
  HStack,
  Skeleton,
  SkeletonText,
  Text,
  useMultiStyleConfig,
  VStack,
} from '@chakra-ui/react';

import {formatJobLocation} from './format-job-location';

const jobDetailsMessages = defineMessages({
  notFoundTitle: {
    id: 'job_details.not_found.title',
    defaultMessage: 'Job Not Found',
  },
  notFoundBody: {
    id: 'job_details.not_found.body',
    defaultMessage: "The job you're looking for is no longer available.",
  },
  metaType: {
    id: 'job_details.meta.type_of_position',
    defaultMessage: 'Type of position',
  },
  metaDepartment: {
    id: 'job_details.meta.department',
    defaultMessage: 'Department',
  },
  metaLocation: {
    id: 'job_details.meta.location',
    defaultMessage: 'Location',
  },
  metaDatePosted: {
    id: 'job_details.meta.date_posted',
    defaultMessage: 'Date Posted',
  },
});

/**
 * JobDetails component displaying job information
 *
 * @param {Object} props - Component props
 * @param {Object} props.job - Job details object
 * @param {boolean} props.isLoading - Loading state
 */
const JobDetails = ({job, isLoading = false}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('JobDetailsPage', {});

  if (isLoading) {
    return (
      <Box id="careeerDetails" {...styles.loadingRoot}>
        <Skeleton {...styles.loadingTitleSkeleton} />
        <VStack {...styles.loadingMetaStack}>
          {[1, 2, 3, 4].map((i) => (
            <HStack key={i} {...styles.loadingMetaRow}>
              <Skeleton {...styles.loadingLabelSkeleton} />
              <Skeleton {...styles.loadingValueSkeleton} />
            </HStack>
          ))}
        </VStack>
        <SkeletonText {...styles.loadingBodySkeleton} />
      </Box>
    );
  }

  if (!job) {
    return (
      <Box id="careeerDetails" {...styles.jobDetailsRoot}>
        <Box {...styles.emptyStateBox}>
          <Heading {...styles.emptyStateTitle}>
            <FormattedMessage {...jobDetailsMessages.notFoundTitle} />
          </Heading>
          <Text {...styles.emptyStateBody}>
            <FormattedMessage {...jobDetailsMessages.notFoundBody} />
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box id="careeerDetails" {...styles.jobDetailsRoot}>
      <Heading {...styles.jobTitle}>{job.title}</Heading>

      <VStack {...styles.metaStack}>
        <MetaItem
          label={<FormattedMessage {...jobDetailsMessages.metaType} />}
          value={job.workType || job.employmentType}
        />
        <MetaItem
          label={<FormattedMessage {...jobDetailsMessages.metaDepartment} />}
          value={job.category || job.department}
        />
        <MetaItem
          label={<FormattedMessage {...jobDetailsMessages.metaLocation} />}
          value={formatJobLocation(job.location) || null}
        />
        <MetaItem
          label={<FormattedMessage {...jobDetailsMessages.metaDatePosted} />}
          value={job.postedAt ? formatDate(job.postedAt, intl.locale) : null}
        />
      </VStack>

      <Divider {...styles.detailDivider} />

      <VStack {...styles.bodyStack}>
        {job.bulletPoints && job.bulletPoints.length > 0 && (
          <Box {...styles.bulletList}>
            {job.bulletPoints.map((point, index) => (
              <Box key={index} {...styles.bulletListItem}>
                <Text {...styles.bodyText}>{point}</Text>
              </Box>
            ))}
          </Box>
        )}

        {job.description && (
          <Box
            {...styles.descriptionBox}
            dangerouslySetInnerHTML={{__html: job.description}}
          />
        )}

        {!job.description && job.summary && (
          <Text {...styles.bodyText}>{job.summary}</Text>
        )}
      </VStack>
    </Box>
  );
};

const MetaItem = ({label, value}) => {
  if (!value) return null;

  const styles = useMultiStyleConfig('JobDetailsPage', {});

  return (
    <HStack {...styles.metaRow}>
      <Text {...styles.metaLabel}>{label}:</Text>
      <Text {...styles.metaValue}>{value}</Text>
    </HStack>
  );
};

MetaItem.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  value: PropTypes.string,
};

/**
 * Format date to readable string
 */
function formatDate(dateString, locale) {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale || undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

JobDetails.propTypes = {
  job: PropTypes.shape({
    title: PropTypes.string,
    workType: PropTypes.string,
    employmentType: PropTypes.string,
    category: PropTypes.string,
    department: PropTypes.string,
    location: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        city: PropTypes.string,
        state: PropTypes.string,
        countryCode: PropTypes.string,
      }),
    ]),
    postedAt: PropTypes.string,
    description: PropTypes.string,
    summary: PropTypes.string,
    bulletPoints: PropTypes.arrayOf(PropTypes.string),
  }),
  isLoading: PropTypes.bool,
};

export default JobDetails;
